<script setup lang="ts">
/**
 * Selections card redesign — RedisignHomePage `15:9293`
 * Editorial lifestyle photo + title/desc · Default/Hover · Default/Mobile size
 * ≠ FairCapsuleCardV2 (look canvas) · ≠ FairProductCardV2 · ≠ FairSelectionCard (v1)
 */
import { computed, ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { RouterLink } from 'vue-router'
import { FairImage } from '@/ui/primitives/FairImage'
import type {
  FairSelectionsCardV2PreviewState,
  FairSelectionsCardV2Size,
} from '@/ui/tokens/selections-card-v2.data'

interface FairSelectionsCardV2Props {
  imageUrl?: string
  imageAlt?: string
  title?: string
  description?: string
  size?: FairSelectionsCardV2Size
  interactive?: boolean
  /** Histoire / visual QA — force Default or Hover without pointer */
  previewState?: FairSelectionsCardV2PreviewState
  ariaLabel?: string
  to?: RouteLocationRaw
}

const props = withDefaults(defineProps<FairSelectionsCardV2Props>(), {
  imageUrl: undefined,
  imageAlt: '',
  title: 'Fall in the city',
  description: 'Relaxed essentials for everyday wear, blending style and ease.',
  size: 'default',
  interactive: true,
  previewState: undefined,
  ariaLabel: undefined,
  to: undefined,
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const isHovered = ref(false)

const resolvedState = computed(() => {
  if (props.previewState) return props.previewState
  return isHovered.value ? 'hovered' : 'default'
})

const rootClass = computed(() => ({
  'fair-selections-card-v2': true,
  [`fair-selections-card-v2--${props.size}`]: true,
  [`fair-selections-card-v2--${resolvedState.value}`]: true,
  'fair-selections-card-v2--non-interactive': !props.interactive,
}))

function handleRootClick(event: MouseEvent) {
  if (!props.interactive) return
  emit('click', event)
}

function onPointerEnter() {
  if (!props.interactive || props.previewState) return
  isHovered.value = true
}

function onPointerLeave() {
  if (!props.interactive || props.previewState) return
  isHovered.value = false
}
</script>

<template>
  <component
    :is="to ? RouterLink : 'article'"
    :class="rootClass"
    :to="to"
    :aria-label="ariaLabel ?? title"
    @click="handleRootClick"
    @pointerenter="onPointerEnter"
    @pointerleave="onPointerLeave"
  >
    <div
      class="fair-selections-card-v2__look"
      aria-hidden="true"
    >
      <FairImage
        v-if="imageUrl"
        class="fair-selections-card-v2__img"
        :src="imageUrl"
        :alt="imageAlt"
        loading="lazy"
      />
    </div>

    <div class="fair-selections-card-v2__content">
      <p class="fair-selections-card-v2__title">
        {{ title }}
      </p>
      <p class="fair-selections-card-v2__description">
        {{ description }}
      </p>
    </div>
  </component>
</template>
